import type { PartRunPhase } from "../../rail/PartRunPhase/phase";
import { partRunPhase } from "../../rail/PartRunPhase/phase";
import type { RailLayout } from "@/data/rail-branches";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { characterGroupsAtPlayhead } from "../../character/ActiveCharacterGroup/atPlayhead";
import {
  locationBranchKey,
  locationRunRole,
} from "../LocationRun/branchKey";
import { allLocationRuns } from "../LocationRun/catalog";
import { runUsesCastColumn } from "../LocationPlacement/plan";

export type ActiveLocationMemberPhase = "site" | PartRunPhase["kind"];

export interface ActiveLocationMember {
  runId: string;
  role: "primary" | "secondary";
  phase: ActiveLocationMemberPhase;
  visible: boolean;
}

/** Site + part runs visible at the playhead (on-stage stack and/or rail). */
export interface ActiveLocationGroup {
  id: string;
  branchId: string;
  /** Part cohort when split across multiple groups on one branch. */
  placeId?: string;
  label: string;
  members: ActiveLocationMember[];
}

export function isOnStageMember(
  member: ActiveLocationMember,
  _group: ActiveLocationGroup,
  layout: BookTimelineLayout,
  playheadMs: number,
): boolean {
  if (!member.visible) {
    return false;
  }
  if (member.role === "primary") {
    return runUsesCastColumn(layout, member.runId, playheadMs);
  }
  if (member.phase !== "active") {
    return false;
  }
  return runUsesCastColumn(layout, member.runId, playheadMs);
}

export function branchHasUpcomingSecondary(
  layout: BookTimelineLayout,
  branchId: string,
  playheadMs: number,
): boolean {
  for (const run of allLocationRuns(layout)) {
    if (locationBranchKey(run, layout) !== branchId) {
      continue;
    }
    if (locationRunRole(run) !== "secondary") {
      continue;
    }
    if (partRunPhase(layout.rail, run, playheadMs).kind === "upcoming") {
      return true;
    }
  }
  return false;
}

export function onStageMembers(
  group: ActiveLocationGroup,
  layout: BookTimelineLayout,
  playheadMs: number,
): ActiveLocationMember[] {
  return group.members.filter((member) =>
    isOnStageMember(member, group, layout, playheadMs),
  );
}

export function onStageRunIds(
  group: ActiveLocationGroup,
  layout: BookTimelineLayout,
  playheadMs: number,
): string[] {
  return onStageMembers(group, layout, playheadMs).map((member) => member.runId);
}

export function onStagePrimaryRunId(
  group: ActiveLocationGroup,
  layout: BookTimelineLayout,
  playheadMs: number,
): string | undefined {
  const primary = group.members.find(
    (member) => member.role === "primary" && member.visible,
  );
  if (primary === undefined) {
    return undefined;
  }
  if (!runUsesCastColumn(layout, primary.runId, playheadMs)) {
    return undefined;
  }
  return primary.runId;
}

export function onStageSecondaryRunIds(
  group: ActiveLocationGroup,
  layout: BookTimelineLayout,
  playheadMs: number,
): string[] {
  return group.members
    .filter(
      (member) =>
        member.visible
        && member.role === "secondary"
        && isOnStageMember(member, group, layout, playheadMs),
    )
    .map((member) => member.runId);
}

export function visiblePartRunIds(group: ActiveLocationGroup): string[] {
  return group.members
    .filter((member) => member.visible && member.role === "secondary")
    .map((member) => member.runId);
}

export function visibleMemberRunIds(group: ActiveLocationGroup): string[] {
  return group.members
    .filter((member) => member.visible)
    .map((member) => member.runId);
}

export function onStagePlaceLabels(
  layout: BookTimelineLayout,
  group: ActiveLocationGroup,
  playheadMs: number,
): string[] {
  const labelByRunId = new Map(
    allLocationRuns(layout).map((run) => [run.runId, run.locationLabel]),
  );
  const lines: string[] = [];
  for (const runId of onStageRunIds(group, layout, playheadMs)) {
    const label = labelByRunId.get(runId);
    if (label !== undefined) {
      lines.push(label);
    }
  }
  return lines;
}

export function characterIdsInActiveLocationGroup(
  rail: RailLayout,
  group: ActiveLocationGroup,
  playheadMs: number,
): string[] {
  const ids = new Set<string>();
  for (const cohort of characterGroupsAtPlayhead(rail, playheadMs)) {
    if (cohort.branchId !== group.branchId) {
      continue;
    }
    if (group.placeId !== undefined && cohort.placeId !== group.placeId) {
      continue;
    }
    for (const characterId of cohort.markerIds) {
      ids.add(characterId);
    }
  }
  return [...ids].sort((a, b) => a.localeCompare(b));
}
